import { useCallback, useEffect, useState } from "react";
import {
  ApiError,
  api,
  type ManualPositionView,
  type ManualRangeView,
  type ManualStatus,
} from "../api/client";

/**
 * État du cockpit MANUEL (dual-cockpit 2026-07-30). Calqué sur useAutoMode :
 * poll GET /api/manual/status (positions comprises), armement en deux temps
 * (OTP e-mail + phrase « ARM MANUEL »), actions clôture/avance de stop + range
 * (range-only, owner 2026-09-01 — la seule ouverture est l'automate range).
 * Les erreurs métier (409 : NOT_ARMED, raisons IG, SKIPPED_WIDENING_FORBIDDEN)
 * remontent en {@link ManualActionError} avec le detail du backend.
 */

export const MANUAL_ARM_PHRASE = "ARM MANUEL";

export class ManualActionError extends Error {
  readonly code: string;
  readonly detail: string;
  constructor(code: string, detail: string) {
    super(detail);
    this.code = code;
    this.detail = detail;
  }
}

function toActionError(e: unknown): never {
  if (e instanceof ApiError && typeof e.body === "object" && e.body !== null) {
    const b = e.body as { error?: string; detail?: string };
    throw new ManualActionError(b.error ?? "UNKNOWN", b.detail ?? "Action refusée.");
  }
  throw e;
}

export interface ManualCockpitState {
  status: ManualStatus | null;
  pending: boolean;
  requestOtp: () => Promise<void>;
  arm: (phrase: string, otp: string) => Promise<ManualStatus>;
  disarm: () => Promise<ManualStatus>;
  closePosition: (dealId: string) => Promise<ManualPositionView>;
  advanceStop: (dealId: string, newStop: number) => Promise<ManualPositionView>;
  startRange: (
    floorLevel: number,
    ceilingLevel: number,
    sizePerPointEur: number,
    stopDistancePoints: number,
  ) => Promise<ManualRangeView>;
  stopRange: () => Promise<ManualRangeView>;
  refresh: () => Promise<void>;
}

export function useManualCockpit(pollIntervalMs = 5_000): ManualCockpitState {
  const [status, setStatus] = useState<ManualStatus | null>(null);
  const [pending, setPending] = useState(false);

  const refresh = useCallback(async (): Promise<void> => {
    try {
      const s = await api.manualStatus();
      setStatus(s);
    } catch {
      // Silencieux — la bannière system-state porte l'injoignabilité backend.
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const tick = async () => {
      try {
        const s = await api.manualStatus();
        if (!cancelled) setStatus(s);
      } catch {
        // Silencieux (voir refresh).
      }
    };
    void tick();
    const id = setInterval(tick, pollIntervalMs);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [pollIntervalMs]);

  const requestOtp = useCallback(async (): Promise<void> => {
    await api.requestManualOtp();
  }, []);

  const arm = useCallback(async (phrase: string, otp: string): Promise<ManualStatus> => {
    setPending(true);
    try {
      const s = await api.armManual({ phrase, otp });
      setStatus(s);
      return s;
    } catch (e) {
      toActionError(e);
    } finally {
      setPending(false);
    }
  }, []);

  const disarm = useCallback(async (): Promise<ManualStatus> => {
    setPending(true);
    try {
      const s = await api.disarmManual();
      setStatus(s);
      return s;
    } finally {
      setPending(false);
    }
  }, []);

  const closePosition = useCallback(
    async (dealId: string): Promise<ManualPositionView> => {
      setPending(true);
      try {
        const p = await api.closeManualPosition(dealId);
        await refresh();
        return p;
      } catch (e) {
        toActionError(e);
      } finally {
        setPending(false);
      }
    },
    [refresh],
  );

  const advanceStop = useCallback(
    async (dealId: string, newStop: number): Promise<ManualPositionView> => {
      setPending(true);
      try {
        const p = await api.advanceManualStop(dealId, newStop);
        await refresh();
        return p;
      } catch (e) {
        toActionError(e);
      } finally {
        setPending(false);
      }
    },
    [refresh],
  );

  const startRange = useCallback(
    async (
      floorLevel: number,
      ceilingLevel: number,
      sizePerPointEur: number,
      stopDistancePoints: number,
    ): Promise<ManualRangeView> => {
      setPending(true);
      try {
        const r = await api.startManualRange({
          floorLevel,
          ceilingLevel,
          sizePerPointEur,
          stopDistancePoints,
        });
        await refresh();
        return r;
      } catch (e) {
        toActionError(e);
      } finally {
        setPending(false);
      }
    },
    [refresh],
  );

  const stopRange = useCallback(async (): Promise<ManualRangeView> => {
    setPending(true);
    try {
      const r = await api.stopManualRange();
      await refresh();
      return r;
    } finally {
      setPending(false);
    }
  }, [refresh]);

  return {
    status,
    pending,
    requestOtp,
    arm,
    disarm,
    closePosition,
    advanceStop,
    startRange,
    stopRange,
    refresh,
  };
}
